import { DragEvent, useRef, useState } from "react";
import { convertSize, removeElementAtIndex } from "../../helperFunctions";
import { MAX_FILE_SIZE } from "../../utils/constants";
import { Icon } from "../icons/icon";
import { ErrorText } from "../typography/errorText";

export const FileUploadInput = ({
  maxNumberOfFiles = 1,
  handleChange,
  selectedFiles,
  types = "PNG, JPG",
  accept = "image/*",
  title = "a file",
  errors,
}: {
  maxNumberOfFiles?: number;
  handleChange: (files: File[]) => void;
  selectedFiles?: File[];
  types?: string;
  accept?: string;
  title?: string;
  errors?: string;
}) => {
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [fileErrors, setFileErrors] = useState<string>();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileUpload = (files: File[]) => {
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (files.length > maxNumberOfFiles) {
      setFileErrors(
        `You can only upload a maximum of ${maxNumberOfFiles} files(s)`
      );
      return;
    } else if (oversizedFiles.length > 0) {
      setFileErrors(`Max file size allowed is 5MB`);
      return;
    } else {
      handleChange(files);
      setFileErrors(undefined);
    }
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e?.dataTransfer?.files && (e?.dataTransfer?.files?.length ?? 0) > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  return (
    <div
      className="relative overflow-hidden"
      onClick={() => {
        if (!selectedFiles || selectedFiles?.length < 1) {
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef?.current?.click();
          }
        }
      }}
      onDragEnter={handleDrag}
    >
      {selectedFiles && selectedFiles?.length > 0 ? (
        <div className="py-4 flex flex-col gap-4">
          {selectedFiles.map((file, i) => {
            const keyValue = `selectedFile${i}`;

            return (
              <div
                key={keyValue}
                className="flex items-center justify-between gap-4"
              >
                <div className="w-fit p-[8px] text-pry-green-200 rounded-full bg-light-green-bg">
                  <Icon svg="small-document-icon" width={16} height={16} />
                </div>

                <div className="flex-1">
                  <p className="text-base text-ellipsis overflow-hidden max-w-[228px]">
                    {file?.name}
                  </p>
                  <p className="text-sm mt-2 text-pry-dark-green-300/60">
                    {convertSize(file?.size)} • 100% uploaded
                  </p>
                </div>

                <button
                  type="button"
                  className="
                  w-fit p-[8px] text-red-200 rounded-full
                  bg-lighter-red-bg
                  "
                  onClick={() => {
                    handleChange(removeElementAtIndex(selectedFiles, i));
                  }}
                >
                  <Icon svg="delete-icon" width={20} height={20} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <button
          type="button"
          className={`
            p-4 w-full
            ${dragActive && "!border-pry-green-200 !border-dotted"}
            rounded-[8px] bg-light-green-bg
            flex flex-col justify-center items-center gap-3
            `}
        >
          <Icon
            svg="gallery-icon"
            width={32}
            height={32}
            className="text-pry-green-200"
          />

          <div className="flex flex-col justify-center items-centergap-1">
            <p className="text-base text-pry-dark-green-300/80 text-center">
              <span className="text-pry-green-200">Upload {title} </span>or drag
              and drop
            </p>
            <p className="text-sm text-pry-dark-green-300/80">
              {types} up to 5MB
            </p>
          </div>
        </button>
      )}

      <input
        type="file"
        className="hidden"
        accept={accept}
        ref={fileInputRef}
        multiple={maxNumberOfFiles > 1}
        onChange={(e) => {
          if (!!e?.target?.files && (e?.target?.files?.length ?? 0) > 0) {
            handleFileUpload(Array.from(e.target.files));
          }
        }}
      />

      {dragActive && (
        <div
          className="absolute inset-0 z-10 opacity-0"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        />
      )}

      {!!errors ? <ErrorText text={errors} /> : undefined}

      {!!fileErrors ? <ErrorText text={fileErrors} /> : undefined}
    </div>
  );
};
